import { useEffect } from "react";

/**
 * O TÍTULO DA ABA, NUM LUGAR SÓ — `T8.25` e `T8.39`.
 *
 * O achado, na letra do relatório: *"A aba do navegador mostra só 'HQ-Lab' em /biblioteca, /motores
 * e em várias telas internas, o que dificulta achar a aba certa."* Quem tem seis abas abertas não
 * acha nenhuma se as seis dizem a mesma coisa.
 *
 * ── POR QUE UM HOOK, E NÃO MAIS UM `useEffect` EM CADA TELA ──────────────────────────────────
 *
 * Medido em 26/09 antes de mexer: **dez arquivos** escreviam `document.title` à mão, cada um com o
 * seu `useEffect` e o seu `return` de limpeza, e **cinco rotas simplesmente esqueceram** — a home,
 * `/motores`, o acervo, o "novo quadrinho" e o editor. Em produção, `/` e `/motores` diziam só
 * "HQ-Lab".
 *
 * Isso não é descuido de quem escreveu: é o que dez cópias de três linhas produzem. A décima
 * primeira tela vai esquecer também. Com um hook, esquecer fica visível — é uma linha que não está
 * lá, e um teste consegue cobrar.
 *
 * ── DUAS DECISÕES QUE O NOME DA FUNÇÃO NÃO CONTA ─────────────────────────────────────────────
 *
 * 1. **O sufixo é daqui, não de quem chama.** Cada tela passa só o seu nome ("Meus quadrinhos") e o
 *    "· HQ-Lab" entra aqui. Assim o dia em que o produto mudar de nome é um dia de uma linha, e
 *    ninguém escreve "HQ Lab" com espaço numa tela e "HQ-Lab" noutra.
 *
 * 2. **A limpeza devolve "HQ-Lab" e não o título anterior.** A aba é do produto, não da tela: se
 *    alguém volta para uma rota que não chama este hook, o nome do produto é a resposta honesta —
 *    melhor que o título de uma tela que já saiu de cena.
 *
 *
 * ── POR QUE O NOME COMEÇA EM INGLÊS, NUMA CASA QUE ESCREVE EM PORTUGUÊS ──────────────────────
 *
 * Nasceu `usarTituloDaAba` e o build recusou: a regra `react-hooks/rules-of-hooks` só reconhece
 * hook cujo nome começa com `use`, e sem esse reconhecimento ela para de conferir as chamadas
 * condicionais — que é justamente o erro que ela existe para pegar. Não é preferência de idioma:
 * `use` aqui é marcador de API do React, como o `useEffect` que esta função chama. O que é do
 * domínio continua em português: "título da aba".
 * O `undefined` é aceito de propósito: telas que ainda estão carregando o nome (uma história da
 * biblioteca, por exemplo) chamam com `undefined` no primeiro render e com o nome quando ele chega,
 * sem precisar de `if` em volta do hook — chamada condicional de hook é erro de React.
 */
export const NOME_DO_PRODUTO = "HQ-Lab";

export function useTituloDaAba(nomeDaTela: string | undefined) {
  useEffect(() => {
    document.title = nomeDaTela
      ? `${nomeDaTela} · ${NOME_DO_PRODUTO}`
      : NOME_DO_PRODUTO;
    return () => {
      document.title = NOME_DO_PRODUTO;
    };
  }, [nomeDaTela]);
}
